import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { HASURA_SECRET, HASURA_URL } from '@core/config/constants';
import { getEnvShopId } from '@core/config/shops';
import {
  HASURA_SHOP_CONTEXT_KEY,
  OMIT_HASURA_SHOP_SCOPE_KEY,
  type HasuraApolloContext,
} from './apolloContext';
import { inflightDedupeLink } from './inflightDedupeLink';
import { graphqlRequestLedgerLink } from '@core/observability/graphqlRequestLedger';

const httpLink = createHttpLink({
  uri: HASURA_URL,
});

const authLink = setContext((_operation, prevContext) => {
  const prevHeaders = {
    ...((prevContext as { headers?: Record<string, string> }).headers ?? {}),
  };
  const ctx = prevContext as Readonly<HasuraApolloContext>;
  const headers: Record<string, string> = {
    ...prevHeaders,
    'x-hasura-admin-secret': HASURA_SECRET,
  };
  if (ctx[OMIT_HASURA_SHOP_SCOPE_KEY]) {
    // Full tenant list (e.g. `mst_shop`) — omit session shop if your Hasura setup
    // filters rows by `X-Hasura-Shop-Id`.
    delete headers['x-hasura-shop-id'];
  } else {
    // Prefer explicit per-op shop, then any header already on context, then env.
    // Without this, a missing `hasuraShopId` on Apollo context can overwrite a
    // correct `x-hasura-shop-id` with the configured primary shop id and hide other tenants' rows.
    const shopId =
      ctx[HASURA_SHOP_CONTEXT_KEY] ??
      prevHeaders['x-hasura-shop-id'] ??
      getEnvShopId();
    headers['x-hasura-shop-id'] = shopId;
  }
  return { headers };
});

const clientLinks = import.meta.env.DEV
  ? [inflightDedupeLink, graphqlRequestLedgerLink, authLink, httpLink]
  : [inflightDedupeLink, authLink, httpLink];

/** Single Apollo client for the Phoenix Hasura endpoint. */
const singleton = new ApolloClient({
  link: from(clientLinks),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          mst_product: {
            keyArgs: ['where', 'limit', 'offset', 'order_by'],
          },
          mst_bookings: {
            keyArgs: ['where', 'limit', 'order_by'],
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      fetchPolicy: 'network-only',
    },
  },
});

export function getDefaultGraphqlClient(): ApolloClient {
  return singleton;
}

/** @deprecated Multiplexed backends removed; returns the same client. */
export function getGraphqlClientsByShop(): Map<string, ApolloClient> {
  return new Map([['default', singleton]]);
}

/** @deprecated Multiplexed backends removed; returns the same client. */
export function getGraphqlClientForShop(_sourceKeyOrShopId: string): ApolloClient {
  return singleton;
}
